import React, { useMemo, useState } from "react";
import "../style/AddCar.css";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "../supabase/supabaseClient";
import { signOut } from "firebase/auth";
import { auth } from "../firebase/firebase";

const specOptions = [
  { value: "Vehicle condition", label: "Fahrzeugzustand" },
  { value: "Category", label: "Kategorie" },
  { value: "Series", label: "Baureihe" },
  { value: "Equipment line", label: "Ausstattungslinie" },
  { value: "Origin", label: "Herkunft" },
  { value: "Mileage", label: "Kilometerstand" },
  { value: "Engine displacement", label: "Hubraum" },
  { value: "Performance", label: "Leistung" },
  { value: "Drive type", label: "Antriebsart" },
  { value: "Fuel type", label: "Kraftstoffart" },
  { value: "Energy consumption (combined)", label: "Energieverbrauch kombiniert" },
  { value: "CO₂ emissions (combined)", label: "CO₂-Emissionen kombiniert" },
  { value: "Fuel consumption", label: "Kraftstoffverbrauch" },
  { value: "Number of seats", label: "Anzahl der Sitze" },
  { value: "Number of doors", label: "Anzahl der Türen" },
  { value: "Transmission", label: "Getriebe" },
  { value: "Exterior color", label: "Außenfarbe" },
  { value: "Interior color", label: "Innenfarbe" },
  { value: "Body type", label: "Karosserieform" },
  { value: "First registration", label: "Erstzulassung" },
  { value: "Warranty", label: "Garantie" },
  { value: "Vehicle owner", label: "Fahrzeughalter" },
  { value: "HU", label: "HU" },
  { value: "Air conditioning", label: "Klimaanlage" },
  { value: "Parking assistance", label: "Einparkhilfe" },
  { value: "Airbags", label: "Airbags" },
  { value: "Cylinder", label: "Zylinder" },
];

const equipmentOptions = [
  { value: "Tinted windows", label: "Getönte Scheiben" },
  { value: "ABS", label: "ABS" },
  { value: "Alarm system", label: "Alarmanlage" },
  { value: "Detachable towbar", label: "Abnehmbare Anhängerkupplung" },
  { value: "Armrest", label: "Armlehne" },
  { value: "On-board computer", label: "Bordcomputer" },
  { value: "CD player", label: "CD-Spieler" },
  { value: "Electric window regulator", label: "Elektrische Fensterheber" },
  { value: "Electric side mirrors", label: "Elektrische Seitenspiegel" },
  { value: "Electric immobiliser", label: "Elektrische Wegfahrsperre" },
  { value: "ESP", label: "ESP" },
  { value: "Front-wheel drive", label: "Frontantrieb" },
  { value: "Luggage compartment partition", label: "Gepäckraumabtrennung" },
  { value: "Isofix", label: "Isofix" },
  { value: "Alloy wheels", label: "Leichtmetallfelgen" },
  { value: "Navigation system", label: "Navigationssystem" },
  { value: "Fog lights", label: "Nebelscheinwerfer" },
  { value: "Breakdown kit", label: "Pannenset" },
  { value: "Particle filter", label: "Partikelfilter" },
  { value: "Sunroof", label: "Schiebedach" },
  { value: "Power steering", label: "Servolenkung" },
  { value: "Heated seats", label: "Sitzheizung" },
  { value: "Sound system", label: "Soundsystem" },
  { value: "Cruise control", label: "Tempomat" },
  { value: "Traction control", label: "Traktionskontrolle" },
  { value: "Xenon headlights", label: "Xenon-Scheinwerfer" },
  { value: "Central locking", label: "Zentralverriegelung" },
];

export default function AddCar() {
  const navigate = useNavigate();

  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [formData, setFormData] = useState({
    carName: "",
    brand: "",
    year: new Date().getFullYear(),
    price: "",
    status: "Available",
    description: "",
    imageFiles: [],
  });

  const [previewImages, setPreviewImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [specs, setSpecs] = useState([
    { label: "Vehicle condition", value: "" },
  ]);
  const [selectedEquipment, setSelectedEquipment] = useState([]);

  const usedLabels = useMemo(
    () => specs.map((item) => item.label).filter(Boolean),
    [specs]
  );

  const availableOptionsForRow = (currentLabel) =>
    specOptions.filter(
      (option) =>
        option.value === currentLabel || !usedLabels.includes(option.value)
    );

  const getSpecLabel = (value) => {
    const found = specOptions.find((item) => item.value === value);
    return found ? found.label : value;
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem("admin");
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);

    if (!files.length) return;

    setFormData((prev) => ({
      ...prev,
      imageFiles: files,
    }));

    setPreviewImages(files.map((file) => URL.createObjectURL(file)));
  };

  const removeImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      imageFiles: prev.imageFiles.filter((_, i) => i !== index),
    }));

    setPreviewImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSpecChange = (index, field, value) => {
    setSpecs((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  const addSpecRow = () => {
    setSpecs((prev) => [...prev, { label: "", value: "" }]);
  };

  const removeSpecRow = (index) => {
    if (specs.length === 1) return;
    setSpecs((prev) => prev.filter((_, i) => i !== index));
  };

  const toggleEquipment = (item) => {
    setSelectedEquipment((prev) =>
      prev.includes(item) ? prev.filter((i) => i !== item) : [...prev, item]
    );
  };

  const uploadCarImages = async (files) => {
    const uploadedUrls = [];

    for (const file of files) {
      const fileExt = file.name.split(".").pop();
      const fileName = `${Date.now()}-${crypto.randomUUID()}.${fileExt}`;
      const filePath = `cars/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from("car-images")
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from("car-images")
        .getPublicUrl(filePath);

      uploadedUrls.push(data.publicUrl);
    }

    return uploadedUrls;
  };

  const resetForm = () => {
    setFormData({
      carName: "",
      brand: "",
      year: new Date().getFullYear(),
      price: "",
      status: "Available",
      description: "",
      imageFiles: [],
    });

    setPreviewImages([]);
    setSpecs([{ label: "Vehicle condition", value: "" }]);
    setSelectedEquipment([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.imageFiles.length) {
      alert("Bitte laden Sie mindestens ein Fahrzeugbild hoch.");
      return;
    }

    if (!formData.carName.trim() || !formData.brand.trim() || !formData.price) {
      alert("Bitte füllen Sie alle Pflichtfelder aus.");
      return;
    }

    try {
      setLoading(true);

      const imageUrls = await uploadCarImages(formData.imageFiles);

      const cleanedSpecs = specs.filter(
        (item) => item.label.trim() !== "" && item.value.trim() !== ""
      );

      const technicalData = {};

      cleanedSpecs.forEach((item) => {
        technicalData[item.label] = item.value;
      });

      const carData = {
        car_name: formData.carName.trim(),
        brand: formData.brand.trim(),
        year: Number(formData.year),
        price: Number(formData.price),
        status: formData.status,
        description: formData.description.trim(),
        main_image: imageUrls[0],
        images: imageUrls,
        specifications: cleanedSpecs,
        technical_data: technicalData,
        equipment: selectedEquipment,
      };

      const { error } = await supabase.from("cars").insert([carData]);

      if (error) throw error;

      alert("Auto erfolgreich hinzugefügt!");
      resetForm();
    } catch (error) {
      console.error("Supabase error:", error);

      alert(
        `Supabase Fehler: ${error.code || "unknown"}\n${
          error.message || "Auto konnte nicht hinzugefügt werden."
        }`
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-car-page">
      <button
        type="button"
        className={`admin-hamburger ${sidebarOpen ? "active" : ""}`}
        onClick={() => setSidebarOpen(!sidebarOpen)}
        aria-label="Open admin menu"
      >
        <span></span>
        <span></span>
        <span></span>
      </button>

      {sidebarOpen && (
        <div className="admin-sidebar-overlay" onClick={closeSidebar}></div>
      )}

      <aside className={`admin-sidebar ${sidebarOpen ? "open" : ""}`}>
        <div className="admin-logo-box">
          <div className="admin-logo-icon">🚗</div>

          <div>
            <h3>Adminbereich</h3>
            <p>Elite Motors</p>
          </div>
        </div>

        <nav className="admin-nav">
          <Link
            to="/admin/manage-cars"
            className="admin-nav-link"
            onClick={closeSidebar}
          >
            Autos verwalten
          </Link>

          <Link
            to="/admin/add-car"
            className="admin-nav-link active"
            onClick={closeSidebar}
          >
            Auto hinzufügen
          </Link>
        </nav>

        <div className="admin-sidebar-actions">
          <Link to="/" className="back-site-btn" onClick={closeSidebar}>
            ← Zur Webseite
          </Link>

          <button className="logout-btn" onClick={handleLogout}>
            Abmelden
          </button>
        </div>
      </aside>

      <main className="add-car-content">
        <div className="page-header">
          <h1>Neues Auto hinzufügen</h1>
          <p>Fügen Sie ein neues Fahrzeug zum Bestand hinzu</p>
        </div>

        <form className="add-car-card" onSubmit={handleSubmit}>
          <div className="form-section">
            <h2>Grundinformationen</h2>

            <div className="form-group">
              <label>Autoname</label>

              <input
                type="text"
                name="carName"
                value={formData.carName}
                onChange={handleChange}
                placeholder="z. B. Audi A4 S Line"
                required
              />
            </div>

            <div className="form-group">
              <label>Marke</label>

              <input
                type="text"
                name="brand"
                value={formData.brand}
                onChange={handleChange}
                placeholder="z. B. Audi"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Jahr</label>

                <input
                  type="number"
                  name="year"
                  value={formData.year}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Preis (€)</label>

                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  placeholder="25000"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>Status</label>

              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
              >
                <option value="Available">Verfügbar</option>
                <option value="Sold">Verkauft</option>
                <option value="Reserved">Reserviert</option>
              </select>
            </div>

            <div className="form-group">
              <label>Beschreibung</label>

              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Fahrzeugbeschreibung schreiben..."
                rows="6"
              />
            </div>
          </div>

          <div className="form-section">
            <h2>Autofotos</h2>

            <div className="form-group">
              <label>Bilder hochladen</label>

              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageChange}
              />
            </div>

            {previewImages.length > 0 && (
              <div className="multi-preview-grid">
                {previewImages.map((image, index) => (
                  <div className="multi-preview-card" key={index}>
                    <img
                      src={image}
                      alt={`Vorschau ${index + 1}`}
                      className="multi-preview-image"
                    />

                    <button
                      type="button"
                      className="remove-image-btn"
                      onClick={() => removeImage(index)}
                    >
                      Entfernen
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="form-section">
            <div className="section-head">
              <h2>Technische Daten</h2>

              <button
                type="button"
                className="add-spec-btn"
                onClick={addSpecRow}
              >
                + Statistik hinzufügen
              </button>
            </div>

            <div className="specs-wrapper">
              {specs.map((spec, index) => (
                <div className="spec-row" key={index}>
                  <div className="form-group">
                    <label>Statistikname</label>

                    <select
                      value={spec.label}
                      onChange={(e) =>
                        handleSpecChange(index, "label", e.target.value)
                      }
                    >
                      <option value="">Statistik auswählen</option>

                      {availableOptionsForRow(spec.label).map((option) => (
                        <option key={option.value} value={option.value}>
                          {getSpecLabel(option.value)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Wert</label>

                    <input
                      type="text"
                      value={spec.value}
                      onChange={(e) =>
                        handleSpecChange(index, "value", e.target.value)
                      }
                      placeholder="z. B. Gebrauchtfahrzeug"
                    />
                  </div>

                  <button
                    type="button"
                    className="remove-spec-btn"
                    onClick={() => removeSpecRow(index)}
                    disabled={specs.length === 1}
                  >
                    Entfernen
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="form-section">
            <h2>Ausstattung</h2>

            <div className="equipment-grid">
              {equipmentOptions.map((item) => {
                const active = selectedEquipment.includes(item.value);

                return (
                  <div
                    key={item.value}
                    className={`equipment-item ${active ? "active" : ""}`}
                    onClick={() => toggleEquipment(item.value)}
                  >
                    <span>{item.label}</span>
                    <span className="check">{active ? "✓" : ""}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <button type="submit" className="submit-btn" disabled={loading}>
            {loading ? "Wird hochgeladen & gespeichert..." : "Auto hinzufügen"}
          </button>
        </form>
      </main>
    </div>
  );
}